import React, { useContext, useEffect } from 'react';
import { SxStyle } from '../../types';
import { useUserAuth } from '../../contexts/auth';
import Box from '@mui/material/Box';
import MenuListItem from './menu-list-item';
import Drawer from '@mui/material/Drawer';
import MenuContext from '../../contexts/menu';
import List from '@mui/material/List';
import { useHistory, useLocation } from 'react-router-dom';
import { MenuItem, menuItems, MenuVariant, MenuPermission } from './menu-schema';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
// import MenuListGroupTeam from './menu-list-group-team';
import { drawerWidth } from '../../constants';
import MenuListGroup from './menu-list-group';
import { Paper } from '@mui/material';
import AppSelector from './app-selector';
import { useConfig } from '../../contexts/config';

const styles: SxStyle = {
  menu: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: (theme) => ({
    width: '100%',
    backgroundColor: theme.palette.menu.background,
    justifyContent: 'center',
    flexDirection: 'column',
    alignItems: 'center',
    display: 'flex',
    paddingTop: '20px',
    paddingBottom: '10px',
    '> img': {
      width: '120px',
    },
  }),
  headerText: {
    letterSpacing: '5px',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 'normal',
    textAlign: 'center',
    marginTop: '12px',
    marginBottom: '5px',
    marginRight: '-5px',
  },
  itemsContainer: {
    marginLeft: '10px',
    marginRight: '10px',
    padding: '20px 5px',
    borderRadius: '6px',
    background: '#003168',
  },
  appSelectorBox: {
    paddingBottom: '20px',
    position: 'relative',
  },
  itemsList: {
    display: 'flex',
    flexDirection: 'column',
    // minHeight: 'calc(100% - 194px)',
    paddingTop: 0,
    paddingBottom: 0,
    flex: '1 1 auto',
    gap: 1,
  },
  itemsListExtended: {
    px: '5px',
    marginTop: '10px',
  },
  bottomItemsList: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'flex-end',
  },
  bottomDivider: {
    borderTopStyle: 'solid',
    borderTopWidth: '1px',
    borderTopColor: 'rgba(144, 202, 249, 0.16)',
    marginTop: '5px',
  },
  drawer: (theme) => ({
    '& .MuiDrawer-paper': {
      overflowX: 'hidden',
      width: `${drawerWidth}px`,
      backgroundColor: theme.palette.menu.background,
      color: theme.palette.primary.contrastText,
      alignItems: 'stretch',

      [theme.breakpoints.up('md')]: {
        // Custom scrollbar styling
        scrollbarColor: '#c1c1c1 transparent',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': {
          width: '12px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
          borderRadius: '10px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#c1c1c1',
          borderRadius: '10px',
          border: '2px solid transparent',
          backgroundClip: 'content-box',
        },
        '&::-webkit-scrollbar-thumb:hover': {
          backgroundColor: '#7d7d7d',
        },
      },
    },
  }),
};

const Menu = () => {
  const [openAppSelector, setOpenAppSelector] = React.useState<boolean>(false);
  const { currentUser, isSuperAdmin, isTeamAdmin, isStatsAdmin, hasTeamAccess } = useUserAuth();
  const { selectedAbbrv, setSelectedAbbrv } = useConfig();
  const { mobileOpen, handleDrawerToggle } = useContext(MenuContext);
  const location = useLocation();
  const history = useHistory();
  const [, currentRoute, locationAbbrv] = location.pathname.split('/');
  const hasAccessToMenuItem = (menuItem: MenuItem) => {
    if (!menuItem.permissions) {
      return true;
    }
    if (isSuperAdmin && menuItem.permissions.includes(MenuPermission.SUPER_ADMIN)) {
      return true;
    }
    if (!isSuperAdmin && isTeamAdmin && menuItem.permissions.includes(MenuPermission.TEAM_ADMIN)) {
      if (menuItem.allowedTeamAbbrvs?.length) {
        const teamAbbrv = locationAbbrv || selectedAbbrv;
        return (
          !!teamAbbrv && menuItem.allowedTeamAbbrvs.includes(teamAbbrv) && hasTeamAccess(teamAbbrv)
        );
      }
      return true;
    }
    if (isStatsAdmin && menuItem.permissions.includes(MenuPermission.STATS_ADMIN)) {
      return true;
    }
    return false;
  };
  const topLevelMenuItems = menuItems.filter(hasAccessToMenuItem);
  const allowedMenuItems = menuItems.reduce((acc: MenuItem[], menuItem) => {
    if (menuItem.menuItems?.length) {
      return [...acc, ...menuItem.menuItems.filter(hasAccessToMenuItem)];
    }

    if (hasAccessToMenuItem(menuItem)) {
      return [...acc, menuItem];
    }

    return acc;
  }, []);
  const selectedMenuItem = allowedMenuItems.find((menuItem) => menuItem.id === currentRoute);

  useEffect(() => {
    if (locationAbbrv && selectedMenuItem?.to?.includes('/abbrv')) {
      setSelectedAbbrv(locationAbbrv);
    }
  }, [locationAbbrv, selectedMenuItem, setSelectedAbbrv]);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('NAVIGATE', location);
    }
    if (mobileOpen) {
      handleDrawerToggle();
    }
    // exhaustive-deps ignored
  }, [location]);

  const configsDrawer = (
    <Box sx={styles.menu}>
      <Box sx={styles.header}>
        <img src="/logo-192.png" alt="Svensk Elitfotboll" />
        <Typography sx={styles.headerText}>SUPERADMIN</Typography>
      </Box>
      <Box sx={styles.itemsContainer}>
        <Box sx={styles.appSelectorBox}>
          <AppSelector
            open={openAppSelector}
            setOpen={setOpenAppSelector}
            selectedAbbrv={selectedAbbrv}
            onSelect={(abbrv) => {
              setOpenAppSelector(false);
              if (selectedMenuItem?.to && selectedMenuItem.to.includes('abbrv')) {
                history.push(selectedMenuItem.to.replace('abbrv', abbrv));
              } else {
                setSelectedAbbrv(abbrv);
              }
            }}
          />
        </Box>
        <List sx={{ ...styles.itemsList, display: 'flex' }}>
          {currentUser &&
            topLevelMenuItems.map((menuItem) => (
              <React.Fragment key={`team-${menuItem.to || menuItem.id}`}>
                {menuItem.variant === MenuVariant.ITEM ? (
                  <MenuListItem
                    id={menuItem.id}
                    to={(menuItem.to || '').replace('abbrv', selectedAbbrv || '')}
                    classSelect={menuItem.id}
                    text={menuItem.text}
                    icon={menuItem.icon}
                    isTeam
                  />
                ) : null}
                {(() => {
                  const allowed =
                    menuItem.variant === MenuVariant.TEAM_GROUP &&
                    menuItem.menuItems &&
                    menuItem.menuItems.length > 0;

                  if (!allowed) {
                    return null;
                  }

                  const subMenuItems = menuItem.menuItems?.filter(hasAccessToMenuItem) || [];

                  const open = Boolean(
                    subMenuItems.find((menuItem) => menuItem.id === selectedMenuItem?.id)
                  );
                  return (
                    <MenuListGroup text={menuItem.text} opened={open} isTeam>
                      <List
                        component="div"
                        disablePadding
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                        }}
                      >
                        {subMenuItems.map((subMenuItem) => (
                          <MenuListItem
                            key={menuItem.id + '--' + subMenuItem.id}
                            id={subMenuItem.id}
                            to={(subMenuItem.to || '').replace('abbrv', selectedAbbrv || '')}
                            classSelect={subMenuItem.id}
                            text={subMenuItem.text}
                            icon={subMenuItem.icon}
                            isTeam
                          />
                        ))}
                      </List>
                    </MenuListGroup>
                  );
                })()}
              </React.Fragment>
            ))}
          <Box sx={styles.bottomItemsList} />
        </List>
      </Box>
      <List
        sx={
          {
            ...styles.itemsList,
            ...styles.itemsListExtended,
          } as SxStyle
        }
      >
        {currentUser &&
          topLevelMenuItems.map((menuItem) => (
            <React.Fragment key={`superadmin-${menuItem.to || menuItem.id}`}>
              {menuItem.variant === MenuVariant.SUB_MENU
                ? menuItem.menuItems &&
                  menuItem.menuItems.length > 0 && (
                    <MenuListGroup text={menuItem.text} opened={false}>
                      <List
                        component="div"
                        disablePadding
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '2px',
                        }}
                      >
                        {menuItem.menuItems
                          ?.filter(hasAccessToMenuItem)
                          .map((subMenuItem) => (
                            <MenuListItem
                              key={menuItem.id + '--' + subMenuItem.id}
                              id={subMenuItem.id}
                              to={subMenuItem.to || ''}
                              text={subMenuItem.text}
                              icon={subMenuItem.icon}
                              classSelect={subMenuItem.id}
                              isLink={subMenuItem.isLink}
                              isSubItem
                            />
                          ))}
                      </List>
                    </MenuListGroup>
                  )
                : null}
            </React.Fragment>
          ))}
        <Box sx={styles.bottomItemsList} />
      </List>
      <Box sx={styles.bottomDivider} />
      <MenuListItem
        id="account"
        to="/account"
        classSelect="account"
        text={currentUser?.displayName || currentUser?.email || ''}
        icon={
          <Avatar
            alt={currentUser?.displayName || currentUser?.email || '-'}
            src={currentUser?.photoURL || ''}
          />
        }
      />
    </Box>
  );

  return (
    <Box>
      <Paper sx={{ display: { xs: 'block', sm: 'none' } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          sx={styles.drawer}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
        >
          {configsDrawer}
        </Drawer>
      </Paper>
      <Paper sx={{ display: { xs: 'none', sm: 'block' } }}>
        <Drawer sx={styles.drawer} variant="permanent" open>
          {configsDrawer}
        </Drawer>
      </Paper>
    </Box>
  );
};

export default Menu;
